import { combineReducers } from "redux";
import { RouterState } from "react-router-redux";
import { combineForms } from 'react-redux-form';

/* Common */
import user, { IUserState } from './user';
import trials, { ITrialState } from './trials';
import hrecs, { IHrecState } from './hrecs';
import healthDistricts, { IHealthDistrictState } from './healthDistricts';
import fellowships, { IFellowshipState } from './fellowships';
import projects, { IProjectState } from './projects';
import projectInvestigator, { IProjectInvestigatorState } from './projectInvestigators';
import search, { ISearchState } from './search';
import itemComponents, { IItemComponentState } from './itemComponents';
import categories, { ICategoryState } from './categories';
import personnel, { IPersonnelState } from './personnel';
import personnelRole, { IPersonnelRoleState } from './personnelRoles';
import groups, { IGroupState } from './groups';
import groupItemTemplate, { IGroupItemTemplateState } from './groupItemTemplates';
import groupItemTemplateCategorys, { IGroupItemTemplateCategoryState } from './groupItemTemplateCategorys';
import groupContracts, { IGroupContractState } from './groupContract';
import groupFinances, { IGroupFinanceState } from './groupFinance';
import institutions, { IInstitutionState } from './institutions';
import trialItem, { ITrialItemState } from './trialItems';
import trialGroup, { ITrialGroupState } from './trialGroups';
import trialGroupItem, { ITrialGroupItemState } from './trialGroupItems';
import trialInstitution, { ITrialInstitutionState } from './trialInstitutions';
import trialPersonnel, { ITrialPersonnelState } from './trialPersonnels';
import trialDoi, { ITrialDoiState } from './trialDoi';
import trialWebsite, { ITrialWebsiteState } from './trialWebsite';
import trialInstitutionItem, { ITrialInstitutionItemState } from './trialInstitutionItems';
import collection, { ICollectionState } from './collections';
import collectionInstance, { ICollectionInstanceState } from './collectionInstance';
import countries, { ICountryState } from './countries';
import institutionCodings, { IInstitutionCodingState } from './institutionCodings';
import institutionCodes, { IInstitutionCodeState } from './institutionCode';
import noteTypes, { INoteTypeState } from './noteTypes';
import trialTypes, { ITrialTypeState } from './trialTypes';
import itemComponentTypes, { IItemComponentTypeState } from './itemComponentTypes';
import notes, { INoteState } from './notes';
import trialItemImport, { ITrialItemImportState } from './trialItemImport';
import trialItemUpdate, { ITrialItemUpdateState } from './trialItemUpdate';
import openClinicaUserRole, { IOpenClinicaUserRoleState } from './openClinicaUserRoles';
import states, { IStateState } from './states';
import institutionTypes, { IInstitutionTypeState } from './institutionTypes';
import siteLocations, { ISiteLocationState } from './siteLocation';
import facilityAllianceMembers, { IFacilityAllianceMemberState } from './facilityAllianceMembers';
import keyContacts, { IKeyContactState } from './keyContacts';
import membershipTypes, { IMembershipTypeState } from './membershipTypes';
import professionalDisciplines, { IProfessionalDisciplineState } from './professionalDiscipline';
import interests, { IInterestState } from './interests';
import salutations, { ISalutationState } from './salutations';
import formTemplates, { IFormTemplateState } from './formTemplates';
import trialInstitutionItemCustomView, { ITrialInstitutionItemCustomViewState } from './trialInstitutionItemCustomView';
import roles, { IRoleState } from './roles';
import departments, { IDepartmentState } from './departments';
import departmentContacts, { IDepartmentContactState } from './departmentContacts';
import reportingTagCategories, { IReportingTagCategoryState } from './reportingTagCategories';
import reportingTags, { IReportingTagState } from './reportingTags';
import taskTypes, { ITaskTypeState } from './taskTypes';
import formTemplateFileTypes, { IFormTemplateFileTypeState } from './formTemplateFileTypes';
import trackerTypes, { ITrackerTypeState } from './trackerTypes';
import trackerMilestones, { ITrackerMilestoneState } from './trackerMilestone';
import standardEmailTemplates, { IStandardEmailTemplateState } from './standardEmailTemplate';
import discussions, { IDiscussionState } from './discussions';
import projectItems, { IProjectItemState } from './projectItems';
import projectItemTemplates, { IProjectItemTemplateState } from './projectItemTemplates';
import projectItemTemplateCategorys, { IProjectItemTemplateCategoryState } from './projectItemTemplateCategorys';
import projectItemImport, { IProjectItemImportState } from './projectItemImport';
import navigation, { INavigationDrawerState } from './navigation';
import theme, { IThemeState } from './theme';
import committees, { ICommitteeState } from './committees';
import committeeMeetings, { ICommitteeMeetingState } from './committeeMeetings';
import committeeRegularInvitees, { ICommitteeRegularInviteeState } from './committeeRegularInvitees';
import committeeMeetingAttendances, { ICommitteeMeetingAttendanceState } from './committeeMeetingAttendances';
import committeeMembers, { ICommitteeMemberState } from './committeeMembers';
import committeeMemberRoles, { ICommitteeMemberRoleState } from './committeeMemberRoles';
import groupItems, { IGroupItemState } from './groupItems';
import projectedAccrual, { IProjectedAccrualState } from './projectedAccrual';
import accrualANZ, { IAccrualANZState } from './accrualANZ';
import laboratories, { ILaboratoryState } from './laboratories';
import laboratoryDepartments, { ILaboratoryDepartmentState } from './laboratoryDepartments';
import laboratoryPersonnels, { ILaboratoryPersonnelState } from './laboratoryPersonnels';
import institutionLaboratory, { IInstitutionLaboratoryState } from './institutionLaboratory';
import trialInstitutionLaboratory, { ITrialInstitutionLaboratoryState } from './trialInstitutionLaboratory';
import trialGroupLaboratory, { ITrialGroupLaboratoryState } from './trialGroupLaboratory';
import trialInstitutionLaboratoryPersonnel, { ITrialInstitutionLaboratoryPersonnelState } from './trialInstitutionLaboratoryPersonnels';
import trialGroupLaboratoryPersonnels, { ITrialGroupLaboratoryPersonnelState } from './trialGroupLaboratoryPersonnels';
import trialItemTemplate, { ITrialItemTemplateState } from './trialItemTemplates';
import personnelTraining, { IPersonnelTrainingState } from './personnelTraining';
import institutionProcedure, { IInstitutionProcedureState } from './institutionProcedure';
import institutionFinance, { IInstitutionFinanceState } from './institutionFinance';
import institutionContract, { IInstitutionContractState } from './institutionContract';
import institutionConfidentialityDeed, { IInstitutionConfidentialityDeedState } from './institutionConfidentialityDeed';
import laboratoryFinance, { ILaboratoryFinanceState } from './laboratoryFinance';
import laboratoryContract, { ILaboratoryContractState } from './laboratoryContract';
import laboratoryConfidentialityDeed, { ILaboratoryConfidentialityDeedState } from './laboratoryConfidentialityDeed';
import groupConfidentialityDeed, { IGroupConfidentialityDeedState } from './groupConfidentialityDeed';
import trialFinance, { ITrialFinanceState } from './trialFinance';
import trialInstitutionFinance, { ITrialInstitutionFinanceState } from './trialInstitutionFinance';
import trialInstitutionContract, { ITrialInstitutionContractState } from './trialInstitutionContract';
import trialGroupFinance, { ITrialGroupFinanceState } from './trialGroupFinance';
import trialGroupContract, { ITrialGroupContractState } from './trialGroupContract';
import trialInstitutionLaboratoryFinance, { ITrialInstitutionLaboratoryFinanceState } from './trialInstitutionLaboratoryFinance';
import trialInstitutionLaboratoryContract, { ITrialInstitutionLaboratoryContractState } from './trialInstitutionLaboratoryContract';
import trialGroupLaboratoryFinance, { ITrialGroupLaboratoryFinanceState } from './trialGroupLaboratoryFinance';
import trialGroupLaboratoryContract, { ITrialGroupLaboratoryContractState } from './trialGroupLaboratoryContract';
import institutionItemTemplateCategories, { IInstitutionItemTemplateCategoryState } from './institutionItemTemplateCategories';
import institutionItemTemplate, { IInstitutionItemTemplateState } from './institutionItemTemplates';
import institutionItem, { IInstitutionItemState } from './institutionItems';
import trialInstitutionItemTemplateCategory, { ITrialInstitutionItemTemplateCategoryState } from './trialInstitutionItemTemplateCategories';
import trialInstitutionItemTemplate, { ITrialInstitutionItemTemplateState } from './trialInstitutionItemTemplates';
import trialGroupItemTemplateCategory, { ITrialGroupItemTemplateCategoryState } from './trialGroupItemTemplateCategories';
import trialGroupItemTemplate, { ITrialGroupItemTemplateState } from './trialGroupItemTemplates';
import trialItemCategory, { ITrialItemCategoryState } from './trialItemCategory';
import noteTypeNotes, { INoteTypeNotesState } from './noteTypeNotes';
import trialInstitutionItemCategory, { ITrialInstitutionItemCategoryState } from './trialInstitutionItemCategory';
import trialGroupItemCategory, { ITrialGroupItemCategoryState } from './trialGroupItemCategory';
import institutionItemCategory, { IInstitutionItemCategoryState } from './institutionItemCategory';
import groupItemCategory, { IGroupItemCategoryState } from './groupItemCategory';
import monitoring, { IMonitoringState } from './monitorings';
import monitoringIssue, { IMonitoringIssueState } from './monitoringIssues';
import monitor, { IMonitorState } from './monitors';
import monitoringDocument, { IMonitoringDocumentState } from './monitoringDocuments';
import monitoringIssueDocument, { IMonitoringIssueDocumentState } from './monitoringIssueDocuments';
import patients, { IPatientState } from './patients';
import quillDocument, { IQuillDocumentState } from './quillDocument';
import quillDocumentImage, { IQuillDocumentImageState } from './quillDocumentImage';
import helpDrawer, { IHelpDrawerState } from './helpDrawer';

/* Pages */
import itemComponentPage, { IItemComponentPageState } from './pages/itemComponentPage';
import collectionPage, { ICollectionPageState } from './pages/collectionPage';
import trialInstitutionItemsPage, { ITrialInstitutionItemsPageState } from './pages/trialInstitutionItemsPage';
import trialsPage, { ITrialsPageState } from './pages/trialsPage';
import trialGroupItemsPage, { ITrialGroupItemsPageState } from './pages/trialGroupItemsPage';
import togglePage, { ITogglePageState } from './pages/togglePage';
import pagingPage, { IPagingPageState } from './pages/pagingPage';
import collectionInstanceTrackerPage, { ICollectionInstanceTrackerPageState } from './pages/collectionInstanceTrackerPage';
import collectionInstanceModalPage, { ICollectionInstanceModalPageState } from './pages/collectionInstanceModalPage';
import groupSettingsPage, { IGroupSettingsPageState } from './pages/groupSettingsPage';
import projectSettingsPage, { IProjectSettingsPageState } from './pages/projectSettingsPage';
import trialSettingsPage, { ITrialSettingsPageState } from './pages/trialSettingsPage';
import groupItemsPage, { IGroupItemsPageState } from './pages/groupItemsPage';
import trialItemsPage, { ITrialItemsPageState } from './pages/trialItemsPage';
import institutionItemsPage, { IInstitutionItemsPageState } from './pages/institutionItemsPage';
import trialInstitutionItemCustomViewDataEntryPage, { ITrialInstitutionItemCustomViewDataEntryPageState } from './pages/trialInstitutionItemCustomViewDataEntryPage';


/* Registration forms + other related data */
import { initialCollectionState, ICollectionReduxFormState } from "./reactReduxForms/collection";
import { initialCollectionInstanceState, ICollectionInstanceReduxFormState } from "./reactReduxForms/collectionInstance";
import { initialTrialState, ITrialReduxFormState } from "./reactReduxForms/trial";
import { initialProjectState, IProjectReduxFormState } from "./reactReduxForms/project";
import { initialProjectInvestigatorState, IProjectInvestigatorReduxFormState } from "./reactReduxForms/projectInvestigator";
import { initialTrialItemState, ITrialItemReduxFormState } from "./reactReduxForms/trialItem";
import { initialTrialGroupState, ITrialGroupReduxFormState } from "./reactReduxForms/trialGroup";
import { initialTrialGroupItemState, ITrialGroupItemReduxFormState } from "./reactReduxForms/trialGroupItem";
import { initialTrialPersonnelState, ITrialPersonnelReduxFormState } from "./reactReduxForms/trialPersonnel";
import { initialItemComponentState, IItemComponentReduxFormState } from "./reactReduxForms/itemComponent";
import { initialGroupState, IGroupReduxFormState } from "./reactReduxForms/group";
import { initialGroupItemTemplateState, IGroupItemTemplateReduxFormState } from "./reactReduxForms/groupItemTemplate";
import { initialGroupItemTemplateCategoryState, IGroupItemTemplateCategoryReduxFormState } from "./reactReduxForms/groupItemTemplateCategory";
import { initialGroupContractState, IGroupContractReduxFormState } from "./reactReduxForms/groupContract";
import { initialGroupFinanceState, IGroupFinanceReduxFormState } from "./reactReduxForms/groupFinance";
import { initialCategoryState, ICategoryReduxFormState } from "./reactReduxForms/category";
import { initialInstitutionState, IInstitutionReduxFormState } from "./reactReduxForms/institution";
import { initialTrialInstitutionState, ITrialInstitutionReduxFormState } from "./reactReduxForms/trialInstitution";
import { initialTrialDoiState, ITrialDoiReduxFormState } from "./reactReduxForms/trialDoi";
import { initialTrialWebsiteState, ITrialWebsiteReduxFormState } from "./reactReduxForms/trialWebsite";
import { initialTrialInstitutionItemState, ITrialInstitutionItemReduxFormState } from "./reactReduxForms/trialInstitutionItem";
import { initialPersonnelState, IPersonnelReduxFormState } from "./reactReduxForms/personnel";
import { initialPersonnelRoleState, IPersonnelRoleReduxFormState } from "./reactReduxForms/personnelRole";
import { initialSearchResultState, ISearchResultReduxFormState } from "./reactReduxForms/searchResult";
import { initialCountryState, ICountryReduxFormState } from "./reactReduxForms/country";
import { initialInstitutionCodingState, IInstitutionCodingReduxFormState } from "./reactReduxForms/institutionCoding";
import { initialInstitutionCodeState, IInstitutionCodeReduxFormState } from "./reactReduxForms/institutionCode";
import { initialNoteTypeState, INoteTypeReduxFormState } from "./reactReduxForms/noteType";
import { initialTrialTypeState, ITrialTypeReduxFormState } from "./reactReduxForms/trialType";
import { initialItemComponentTypeState, IItemComponentTypeReduxFormState } from "./reactReduxForms/itemComponentType";
import { initialNoteState, INoteReduxFormState } from "./reactReduxForms/note";
import { initialTrialItemImportState, ITrialItemImportReduxFormState } from "./reactReduxForms/trialItemImport";
import { initialProjectItemImportState, IProjectItemImportReduxFormState } from "./reactReduxForms/projectItemImport";
import { initialTrialItemUpdateState, ITrialItemUpdateReduxFormState } from "./reactReduxForms/trialItemUpdate";
import { initialOpenClinicaUserRoleState, IOpenClinicaUserRoleReduxFormState } from "./reactReduxForms/openClinicaUserRole";
import { initialStateState, IStateReduxFormState } from "./reactReduxForms/state";
import { initialInstitutionTypeState, IInstitutionTypeReduxFormState } from "./reactReduxForms/institutionType";
import { initialSiteLocationState, ISiteLocationReduxFormState } from "./reactReduxForms/siteLocation";
import { initialFacilityAllianceMemberState, IFacilityAllianceMemberReduxFormState } from "./reactReduxForms/facilityAllianceMember";
import { initialKeyContactState, IKeyContactReduxFormState } from "./reactReduxForms/keyContact";
import { initialMembershipTypeState, IMembershipTypeReduxFormState } from "./reactReduxForms/membershipType";
import { initialProfessionalDisciplineState, IProfessionalDisciplineReduxFormState } from "./reactReduxForms/professionalDiscipline";
import { initialInterestState, IInterestReduxFormState } from "./reactReduxForms/interest";
import { initialSalutationState, ISalutationReduxFormState } from "./reactReduxForms/salutations";
import { initialFormTemplateState, IFormTemplateReduxFormState } from "./reactReduxForms/formTemplate";
import { initialTrialInstitutionItemCustomViewState, ITrialInstitutionItemCustomViewReduxFormState } from "./reactReduxForms/trialInstitutionItemCustomView";
import { initialRoleState, IRoleReduxFormState } from "./reactReduxForms/role";
import { initialDepartmentState, IDepartmentReduxFormState } from "./reactReduxForms/department";
import { initialDepartmentContactState, IDepartmentContactReduxFormState } from "./reactReduxForms/departmentContact";
import { initialReportingTagCategoryState, IReportingTagCategoryReduxFormState } from "./reactReduxForms/reportingTagCategory";
import { initialReportingTagState, IReportingTagReduxFormState } from "./reactReduxForms/reportingTag";
import { initialTaskTypeState, ITaskTypeReduxFormState } from "./reactReduxForms/taskType";
import { initialFormTemplateFileTypeState, IFormTemplateFileTypeReduxFormState } from "./reactReduxForms/formTemplateFileType";
import { initialTrackerTypeState, ITrackerTypeReduxFormState } from "./reactReduxForms/trackerType";
import { initialTrackerMilestoneState, ITrackerMilestoneReduxFormState } from "./reactReduxForms/trackerMilestone";
import { initialStandardEmailTemplateState, IStandardEmailTemplateReduxFormState } from "./reactReduxForms/standardEmailTemplate";
import { initialDiscussionState, IDiscussionReduxFormState } from "./reactReduxForms/discussion";
import { initialProjectItemState, IProjectItemReduxFormState } from "./reactReduxForms/projectItem";
import { initialProjectItemTemplateState, IProjectItemTemplateReduxFormState } from "./reactReduxForms/projectItemTemplate";
import { initialProjectItemTemplateCategoryState, IProjectItemTemplateCategoryReduxFormState } from "./reactReduxForms/projectItemTemplateCategory";
import { initialCommitteeState, ICommitteeReduxFormState } from "./reactReduxForms/committee";
import { initialCommitteeMeetingState, ICommitteeMeetingReduxFormState } from "./reactReduxForms/committeeMeeting";
import { initialCommitteeRegularInviteeState, ICommitteeRegularInviteeReduxFormState } from "./reactReduxForms/committeeRegularInvitee";
import { initialCommitteeMeetingAttendanceState, ICommitteeMeetingAttendanceReduxFormState } from "./reactReduxForms/committeeMeetingAttendance";
import { initialCommitteeMemberState, ICommitteeMemberReduxFormState } from "./reactReduxForms/committeeMember";
import { initialCommitteeMemberRoleState, ICommitteeMemberRoleReduxFormState } from "./reactReduxForms/committeeMemberRole";
import { initialGroupItemState, IGroupItemReduxFormState } from "./reactReduxForms/groupItem";
import { initialLaboratoryState, ILaboratoryReduxFormState } from "./reactReduxForms/laboratory";
import { initialLaboratoryDepartmentState, ILaboratoryDepartmentReduxFormState } from "./reactReduxForms/laboratoryDepartment";
import { initialLaboratoryPersonnelState, ILaboratoryPersonnelReduxFormState } from "./reactReduxForms/laboratoryPersonnel";
import { initialInstitutionLaboratoryState, IInstitutionLaboratoryReduxFormState } from "./reactReduxForms/institutionLaboratory";
import { initialTrialInstitutionLaboratoryState, ITrialInstitutionLaboratoryReduxFormState } from "./reactReduxForms/trialInstitutionLaboratory";
import { initialTrialGroupLaboratoryState, ITrialGroupLaboratoryReduxFormState } from "./reactReduxForms/trialGroupLaboratory";
import { initialTrialInstitutionLaboratoryPersonnelState, ITrialInstitutionLaboratoryPersonnelReduxFormState } from "./reactReduxForms/trialInstitutionLaboratoryPersonnel";
import { initialTrialGroupLaboratoryPersonnelState, ITrialGroupLaboratoryPersonnelReduxFormState } from "./reactReduxForms/trialGroupLaboratoryPersonnel";
import { initialTrialItemTemplateState, ITrialItemTemplateReduxFormState } from "./reactReduxForms/trialItemTemplate";
import { initialPersonnelTrainingState, IPersonnelTrainingReduxFormState } from "./reactReduxForms/personnelTraining";
import { initialInstitutionProcedureState, IInstitutionProcedureReduxFormState } from "./reactReduxForms/institutionProcedure";
import { initialInstitutionFinanceState, IInstitutionFinanceReduxFormState } from "./reactReduxForms/institutionFinance";
import { initialInstitutionContractState, IInstitutionContractReduxFormState } from "./reactReduxForms/institutionContract";
import { initialInstitutionConfidentialityDeedState, IInstitutionConfidentialityDeedReduxFormState } from "./reactReduxForms/institutionConfidentialityDeed";
import { initialLaboratoryFinanceState, ILaboratoryFinanceReduxFormState } from "./reactReduxForms/laboratoryFinance";
import { initialLaboratoryContractState, ILaboratoryContractReduxFormState } from "./reactReduxForms/laboratoryContract";
import { initialLaboratoryConfidentialityDeedState, ILaboratoryConfidentialityDeedReduxFormState } from "./reactReduxForms/laboratoryConfidentialityDeed";
import { initialGroupConfidentialityDeedState, IGroupConfidentialityDeedReduxFormState } from "./reactReduxForms/groupConfidentialityDeed";
import { initialTrialFinanceState, ITrialFinanceReduxFormState } from "./reactReduxForms/trialFinance";
import { initialTrialInstitutionFinanceState, ITrialInstitutionFinanceReduxFormState } from "./reactReduxForms/trialInstitutionFinance";
import { initialTrialInstitutionContractState, ITrialInstitutionContractReduxFormState } from "./reactReduxForms/trialInstitutionContract";
import { initialTrialGroupFinanceState, ITrialGroupFinanceReduxFormState } from "./reactReduxForms/trialGroupFinance";
import { initialTrialGroupContractState, ITrialGroupContractReduxFormState } from "./reactReduxForms/trialGroupContract";
import { initialTrialInstitutionLaboratoryFinanceState, ITrialInstitutionLaboratoryFinanceReduxFormState } from "./reactReduxForms/trialInstitutionLaboratoryFinance";
import { initialTrialInstitutionLaboratoryContractState, ITrialInstitutionLaboratoryContractReduxFormState } from "./reactReduxForms/trialInstitutionLaboratoryContract";
import { initialTrialGroupLaboratoryFinanceState, ITrialGroupLaboratoryFinanceReduxFormState } from "./reactReduxForms/trialGroupLaboratoryFinance";
import { initialTrialGroupLaboratoryContractState, ITrialGroupLaboratoryContractReduxFormState } from "./reactReduxForms/trialGroupLaboratoryContract";
import { initialInstitutionItemTemplateCategoryState, IInstitutionItemTemplateCategoryReduxFormState } from "./reactReduxForms/institutionItemTemplateCategory";
import { initialInstitutionItemTemplateState, IInstitutionItemTemplateReduxFormState } from "./reactReduxForms/institutionItemTemplate";
import { initialInstitutionItemState, IInstitutionItemReduxFormState } from "./reactReduxForms/institutionItem";
import { initialTrialInstitutionItemTemplateCategoryState, ITrialInstitutionItemTemplateCategoryReduxFormState } from "./reactReduxForms/trialInstitutionItemTemplateCategory";
import { initialTrialInstitutionItemTemplateState, ITrialInstitutionItemTemplateReduxFormState } from "./reactReduxForms/trialInstitutionItemTemplate";
import { initialTrialGroupItemTemplateCategoryState, ITrialGroupItemTemplateCategoryReduxFormState } from "./reactReduxForms/trialGroupItemTemplateCategory";
import { initialTrialGroupItemTemplateState, ITrialGroupItemTemplateReduxFormState } from "./reactReduxForms/trialGroupItemTemplate";
import { initialTrialItemCategoryState, ITrialItemCategoryReduxFormState } from "./reactReduxForms/trialItemCategory";
import { initialNoteTypeNotesState, INoteTypeNotesReduxFormState } from "./reactReduxForms/noteTypeNotes";
import { initialTrialInstitutionItemCategoryState, ITrialInstitutionItemCategoryReduxFormState } from "./reactReduxForms/trialInstitutionItemCategory";
import { initialTrialGroupItemCategoryState, ITrialGroupItemCategoryReduxFormState } from "./reactReduxForms/trialGroupItemCategory";
import { initialInstitutionItemCategoryState, IInstitutionItemCategoryReduxFormState } from "./reactReduxForms/institutionItemCategory";
import { initialGroupItemCategoryState, IGroupItemCategoryReduxFormState } from "./reactReduxForms/groupItemCategory";
import { initialMonitoringState, IMonitoringReduxFormState } from "./reactReduxForms/monitoring";
import { initialMonitoringIssueState, IMonitoringIssueReduxFormState } from "./reactReduxForms/monitoringIssue";
import { initialMonitorState, IMonitorReduxFormState } from "./reactReduxForms/monitor";
import { initialMonitoringDocumentState, IMonitoringDocumentReduxFormState } from "./reactReduxForms/monitoringDocument";
import { initialMonitoringIssueDocumentState, IMonitoringIssueDocumentReduxFormState } from "./reactReduxForms/monitoringIssueDocument";
import { initialQuillDocumentState, IQuillDocumentReduxFormState } from "./reactReduxForms/quillDocument";
import { initialQuillDocumentImageState, IQuillDocumentImageReduxFormState } from "./reactReduxForms/quillDocumentImage";

import { RequestState } from "../enumerations/RequestState";
import * as Dtos from "../dtos/Tmd.dtos";

import { IRegistrationFormState } from "./reactReduxForms/index";
import RoutingReducer from "./routing";
import parentTrials, { IParentTrialsState } from "./parentTrials";
import { IHrecReduxFormState, initialHrecState } from "./reactReduxForms/hrec";
import { IHealthDistrictReduxFormState, initialHealthDistrictState } from "./reactReduxForms/healthDistrict";
import { IFellowshipReduxFormState, initialFellowshipState } from "./reactReduxForms/fellowship";
import memberStatuses, { IMemberStatusState } from "./memberStatuses";


export interface IRequestState {
    status: RequestState;
    errorCode: string;
    errorMessage: string;
    errors: Dtos.ResponseError[];
    meta: {[index: string]: string};
}

export interface IFormFilter {
    search: string;
    showInActive: boolean;
}

interface IRegistrationFormsState {
    trials: ITrialState;
    hrecs: IHrecState;
    healthDistrict: IHealthDistrictState;
    fellowship: IFellowshipState;
    projects: IProjectState;
    projectInvestigator: IProjectInvestigatorState;
    itemComponent: IItemComponentState;
    categories: ICategoryState;
    countries: ICountryState;
    institutionCodings: IInstitutionCodingState;
    institutionCodes: IInstitutionCodeState;
    noteTypes: INoteTypeState;
    notes: INoteState;
    trialTypes: ITrialTypeState;
    itemComponentTypes: IItemComponentTypeState;
    groups: IGroupState;
    groupItemTemplate: IGroupItemTemplateState;
    groupItemTemplateCategorys: IGroupItemTemplateCategoryState;
    groupContract: IGroupContractState;
    groupFinance: IGroupFinanceState;
    collection: ICollectionState;
    collectionInstance: ICollectionInstanceState;
    trialItem: ITrialItemState;
    trialGroup: ITrialGroupState;
    trialGroupItem: ITrialGroupItemState;
    trialPersonnel: ITrialPersonnelState;
    institutions: IInstitutionState;
    trialInstitution: ITrialInstitutionState;
    trialDoi: ITrialDoiState;
    trialWebsite: ITrialWebsiteState;
    trialInstitutionItem: ITrialInstitutionItemState;
    personnel: IPersonnelState;
    personnelRole: IPersonnelRoleState;
    search: ISearchState;
    trialItemImport: ITrialItemImportState;
    projectItemImport: IProjectItemImportState;
    trialItemUpdate: ITrialItemUpdateState;
    openClinicaUserRoles: IOpenClinicaUserRoleState;
    states: IStateState;
    institutionTypes: IInstitutionTypeState;
    siteLocations: ISiteLocationState;
    facilityAllianceMembers: IFacilityAllianceMemberState;
    keyContacts: IKeyContactState;
    membershipTypes: IMembershipTypeState;
    memberStatuses: IMemberStatusState;
    professionalDisciplines: IProfessionalDisciplineState;
    interests: IInterestState;
    salutations: ISalutationState;
    formTemplates: IFormTemplateState;
    trialInstitutionItemCustomView: ITrialInstitutionItemCustomViewState;
    roles: IRoleState;
    departments: IDepartmentState;
    departmentContacts: IDepartmentContactState;
    reportingTagCategories: IReportingTagCategoryState;
    reportingTags: IReportingTagState;
    taskTypes: ITaskTypeState;
    formTemplateFileTypes: IFormTemplateFileTypeState;
    trackerTypes: ITrackerTypeState;
    trackerMilestones: ITrackerMilestoneState;
    standardEmailTemplates: IStandardEmailTemplateState;
    discussion: IDiscussionState;
    projectItems: IProjectItemState;
    projectItemTemplates: IProjectItemTemplateState;
    projectItemTemplateCategorys: IProjectItemTemplateCategoryState;
    committees: ICommitteeState;
    committeeMeeting: ICommitteeMeetingState;
    committeeRegularInvitee: ICommitteeRegularInviteeState;
    committeeMeetingAttendance: ICommitteeMeetingAttendanceState;
    committeeMember: ICommitteeMemberState;
    committeeMemberRole: ICommitteeMemberRoleState;
    groupItems: IGroupItemState;
    laboratories: ILaboratoryState;
    laboratoryDepartments: ILaboratoryDepartmentState;
    laboratoryPersonnels: ILaboratoryPersonnelState;
    institutionLaboratory: IInstitutionLaboratoryState;
    trialInstitutionLaboratory: ITrialInstitutionLaboratoryState;
    trialGroupLaboratory: ITrialGroupLaboratoryState;
    trialInstitutionLaboratoryPersonnel: ITrialInstitutionLaboratoryPersonnelState;
    trialGroupLaboratoryPersonnel: ITrialGroupLaboratoryPersonnelState;
    trialItemTemplate: ITrialItemTemplateState;
    personnelTraining: IPersonnelTrainingState;
    institutionProcedure: IInstitutionProcedureState;
    institutionFinance: IInstitutionFinanceState;
    institutionContract: IInstitutionContractState;
    institutionConfidentialityDeed: IInstitutionConfidentialityDeedState;
    laboratoryFinance: ILaboratoryFinanceState;
    laboratoryContract: ILaboratoryContractState;
    laboratoryConfidentialityDeed: ILaboratoryConfidentialityDeedState;
    groupConfidentialityDeed: IGroupConfidentialityDeedState;
    trialFinance: ITrialFinanceState;
    trialInstitutionFinance: ITrialInstitutionFinanceState;
    trialInstitutionContract: ITrialInstitutionContractState;
    trialGroupFinance: ITrialGroupFinanceState;
    trialGroupContract: ITrialGroupContractState;
    trialInstitutionLaboratoryFinance: ITrialInstitutionLaboratoryFinanceState;
    trialInstitutionLaboratoryContract: ITrialInstitutionLaboratoryContractState;
    trialGroupLaboratoryFinance: ITrialGroupLaboratoryFinanceState;
    trialGroupLaboratoryContract: ITrialGroupLaboratoryContractState;
    institutionItemTemplateCategories: IInstitutionItemTemplateCategoryState;
    institutionItemTemplate: IInstitutionItemTemplateState;
    institutionItem: IInstitutionItemState;
    trialInstitutionItemTemplateCategory: ITrialInstitutionItemTemplateCategoryState;
    trialInstitutionItemTemplate: ITrialInstitutionItemTemplateState;
    trialGroupItemTemplateCategory: ITrialGroupItemTemplateCategoryState;
    trialGroupItemTemplate: ITrialGroupItemTemplateState;
    trialItemCategory: ITrialItemCategoryState;
    noteTypeNotes: INoteTypeNotesState;
    trialInstitutionItemCategory: ITrialInstitutionItemCategoryState;
    trialGroupItemCategory: ITrialGroupItemCategoryState;
    institutionItemCategory: IInstitutionItemCategoryState;
    groupItemCategory: IGroupItemCategoryState;
    monitoring: IMonitoringState;
    monitoringIssue: IMonitoringIssueState;
    monitor: IMonitorState;
    monitoringDocument: IMonitoringDocumentState;
    monitoringIssueDocument: IMonitoringIssueDocumentState;
    quillDocument: IQuillDocumentState;
    quillDocumentImage: IQuillDocumentImageState;
}

interface IReduxFormsState {
    itemComponent: Dtos.ItemComponent & IRegistrationFormState;
    collection: Dtos.Collection & IRegistrationFormState;
    collectionInstance: Dtos.CollectionInstance & IRegistrationFormState;
    trial: Dtos.Trial & IRegistrationFormState;
    hrec: Dtos.Hrec & IRegistrationFormState;
    healthDistrict: Dtos.HealthDistrict & IRegistrationFormState;
    fellowship: Dtos.Fellowship & IRegistrationFormState;
    project: Dtos.Project & IRegistrationFormState;
    projectInvestigator: Dtos.ProjectInvestigator & IRegistrationFormState;
    trialItem: Dtos.TrialItem & IRegistrationFormState;
    trialGroup: Dtos.TrialGroup & IRegistrationFormState;
    trialGroupItem: Dtos.TrialGroupItem & IRegistrationFormState;
    trialPersonnel: Dtos.TrialPersonnel & IRegistrationFormState;
    group: Dtos.Group & IRegistrationFormState;
    groupItemTemplate: Dtos.GroupItemTemplate & IRegistrationFormState;
    groupItemTemplateCategory: Dtos.GroupItemTemplateCategory & IRegistrationFormState;
    groupContract: Dtos.GroupContract & IRegistrationFormState;
    groupFinance: Dtos.GroupFinance & IRegistrationFormState;
    category: Dtos.Category & IRegistrationFormState;
    country: Dtos.Country & IRegistrationFormState;
    institutionCoding: Dtos.InstitutionCoding & IRegistrationFormState;
    institutionCode: Dtos.InstitutionCode & IRegistrationFormState;
    noteType: Dtos.NoteType & IRegistrationFormState;
    note: Dtos.Note & IRegistrationFormState;
    trialType: Dtos.TrialType & IRegistrationFormState;
    itemComponentType: Dtos.ItemComponentType & IRegistrationFormState;
    institution: Dtos.Institution & IRegistrationFormState;
    trialInstitution: Dtos.TrialInstitution & IRegistrationFormState;
    trialDoi: Dtos.TrialDoi & IRegistrationFormState;
    trialWebsite: Dtos.TrialWebsite & IRegistrationFormState;
    trialInstitutionItem: Dtos.TrialInstitutionItem & IRegistrationFormState;
    personnel: Dtos.Personnel & IRegistrationFormState;
    personnelRole: Dtos.PersonnelRole & IRegistrationFormState;
    searchResult: Dtos.SearchResult & IRegistrationFormState;
    trialItemImport: Dtos.TrialItemImport & IRegistrationFormState;
    projectItemImport: Dtos.ProjectItemImport & IRegistrationFormState;
    trialItemUpdate: Dtos.TrialItemUpdate & IRegistrationFormState;
    openClinciaUserRole: Dtos.OpenClinicaUserRole & IRegistrationFormState;
    state: Dtos.State & IRegistrationFormState;
    institutionType: Dtos.InstitutionType & IRegistrationFormState;
    siteLocation: Dtos.SiteLocation & IRegistrationFormState;
    facilityAllianceMember: Dtos.FacilityAllianceMember & IRegistrationFormState;
    keyContact: Dtos.KeyContact & IRegistrationFormState;
    membershipType: Dtos.MembershipType & IRegistrationFormState;
    professionalDiscipline: Dtos.ProfessionalDiscipline & IRegistrationFormState;
    interest: Dtos.Interest & IRegistrationFormState;
    salutation: Dtos.Salutation & IRegistrationFormState;
    formTemplate: Dtos.FormTemplate & IRegistrationFormState;
    trialInstitutionItemCustomView: Dtos.TrialInstitutionItemCustomView & IRegistrationFormState;
    role: Dtos.Role & IRegistrationFormState;
    department: Dtos.Department & IRegistrationFormState;
    departmentContact: Dtos.DepartmentContact & IRegistrationFormState;
    reportingTagCategory: Dtos.ReportingTagCategory & IRegistrationFormState;
    reportingTag: Dtos.ReportingTag & IRegistrationFormState;
    taskType: Dtos.TaskType & IRegistrationFormState;
    formTemplateFileType: Dtos.FormTemplateFileType & IRegistrationFormState;
    trackerType: Dtos.TrackerType & IRegistrationFormState;
    trackerMilestone: Dtos.TrackerMilestone & IRegistrationFormState;
    standardEmailTemplate: Dtos.StandardEmailTemplate & IRegistrationFormState;
    discussion: Dtos.Discussion & IRegistrationFormState;
    projectItem: Dtos.ProjectItem & IRegistrationFormState;
    projectItemTemplate: Dtos.ProjectItemTemplate & IRegistrationFormState;
    projectItemTemplateCategory: Dtos.ProjectItemTemplateCategory & IRegistrationFormState;
    committee: Dtos.Committee & IRegistrationFormState;
    committeeMeeting: Dtos.CommitteeMeeting & IRegistrationFormState;
    committeeRegularInvitee: Dtos.CommitteeRegularInvitee & IRegistrationFormState;
    committeeMeetingAttendance: Dtos.CommitteeMeetingAttendance & IRegistrationFormState;
    committeeMember: Dtos.CommitteeMember & IRegistrationFormState;
    committeeMemberRole: Dtos.CommitteeMemberRole & IRegistrationFormState;
    groupItem: Dtos.GroupItem & IRegistrationFormState;
    laboratory: Dtos.Laboratory & IRegistrationFormState;
    laboratoryDepartment: Dtos.LaboratoryDepartment & IRegistrationFormState;
    laboratoryPersonnel: Dtos.LaboratoryPersonnel & IRegistrationFormState;
    institutionLaboratory: Dtos.InstitutionLaboratory & IRegistrationFormState;
    trialInstitutionLaboratory: Dtos.TrialInstitutionLaboratory & IRegistrationFormState;
    trialGroupLaboratory: Dtos.TrialGroupLaboratory & IRegistrationFormState;
    trialInstitutionLaboratoryPersonnel: Dtos.TrialInstitutionLaboratoryPersonnel & IRegistrationFormState;
    trialGroupLaboratoryPersonnel: Dtos.TrialGroupLaboratoryPersonnel & IRegistrationFormState;
    trialItemTemplate: Dtos.TrialItemTemplate & IRegistrationFormState;
    personnelTraining: Dtos.PersonnelTraining & IRegistrationFormState;
    institutionProcedure: Dtos.InstitutionProcedure & IRegistrationFormState;
    institutionFinance: Dtos.InstitutionFinance & IRegistrationFormState;
    institutionContract: Dtos.InstitutionContract & IRegistrationFormState;
    institutionConfidentialityDeed: Dtos.InstitutionConfidentialityDeed & IRegistrationFormState;
    laboratoryFinance: Dtos.LaboratoryFinance & IRegistrationFormState;
    laboratoryContract: Dtos.LaboratoryContract & IRegistrationFormState;
    laboratoryConfidentialityDeed: Dtos.LaboratoryConfidentialityDeed & IRegistrationFormState;
    groupConfidentialityDeed: Dtos.GroupConfidentialityDeed & IRegistrationFormState;
    trialFinance: Dtos.TrialFinance & IRegistrationFormState;
    trialInstitutionFinance: Dtos.TrialInstitutionFinance & IRegistrationFormState;
    trialInstitutionContract: Dtos.TrialInstitutionContract & IRegistrationFormState;
    trialGroupFinance: Dtos.TrialGroupFinance & IRegistrationFormState;
    trialGroupContract: Dtos.TrialGroupContract & IRegistrationFormState;
    trialInstitutionLaboratoryFinance: Dtos.TrialInstitutionLaboratoryFinance & IRegistrationFormState;
    trialInstitutionLaboratoryContract: Dtos.TrialInstitutionLaboratoryContract & IRegistrationFormState;
    trialGroupLaboratoryFinance: Dtos.TrialGroupLaboratoryFinance & IRegistrationFormState;
    trialGroupLaboratoryContract: Dtos.TrialGroupLaboratoryContract & IRegistrationFormState;
    institutionItemTemplateCategory: Dtos.InstitutionItemTemplateCategory & IRegistrationFormState;
    institutionItemTemplate: Dtos.InstitutionItemTemplate & IRegistrationFormState;
    institutionItem: Dtos.InstitutionItem & IRegistrationFormState;
    trialInstitutionItemTemplateCategory: Dtos.TrialInstitutionItemTemplateCategory & IRegistrationFormState;
    trialInstitutionItemTemplate: Dtos.TrialInstitutionItemTemplate & IRegistrationFormState;
    trialGroupItemTemplateCategory: Dtos.TrialGroupItemTemplateCategory & IRegistrationFormState;
    trialGroupItemTemplate: Dtos.TrialGroupItemTemplate & IRegistrationFormState;
    trialItemCategory: Dtos.TrialItemCategory & IRegistrationFormState;
    noteTypeNotes: Dtos.NoteTypeNotes & IRegistrationFormState;
    trialInstitutionItemCategory: Dtos.TrialInstitutionItemCategory & IRegistrationFormState;
    trialGroupItemCategory: Dtos.TrialGroupItemCategory & IRegistrationFormState;
    institutionItemCategory: Dtos.InstitutionItemCategory & IRegistrationFormState;
    groupItemCategory: Dtos.GroupItemCategory & IRegistrationFormState;
    monitoring: Dtos.Monitoring & IRegistrationFormState;
    monitoringIssue: Dtos.MonitoringIssue & IRegistrationFormState;
    monitor: Dtos.Monitor & IRegistrationFormState;
    monitoringDocument: Dtos.MonitoringDocument & IRegistrationFormState;
    monitoringIssueDocument: Dtos.MonitoringIssueDocument & IRegistrationFormState;
    quillDocument: Dtos.QuillDocument & IRegistrationFormState;
    quillDocumentImage: Dtos.QuillDocumentImage & IRegistrationFormState;

    formState: {
        itemComponent: IItemComponentReduxFormState;
        collection: ICollectionReduxFormState
        collectionInstance: ICollectionInstanceReduxFormState;
        trial: ITrialReduxFormState;
        hrec: IHrecReduxFormState;
        healthDistrict: IHealthDistrictReduxFormState;
        fellowship: IFellowshipReduxFormState;
        project: IProjectReduxFormState;
        projectInvestigator: IProjectInvestigatorReduxFormState;
        trialItem: ITrialItemReduxFormState;
        trialGroup: ITrialGroupReduxFormState;
        trialGroupItem: ITrialGroupItemReduxFormState;
        trialPersonnel: ITrialPersonnelReduxFormState;
        group: IGroupReduxFormState;
        groupItemTemplate: IGroupItemTemplateReduxFormState;
        groupItemTemplateCategory: IGroupItemTemplateCategoryReduxFormState;
        groupContract: IGroupContractReduxFormState;
        groupFinance: IGroupFinanceReduxFormState;
        category: ICategoryReduxFormState;
        country: ICountryReduxFormState;
        institutionCoding: IInstitutionCodingReduxFormState;
        institutionCode: IInstitutionCodeReduxFormState;
        noteType: INoteTypeReduxFormState;
        note: INoteReduxFormState;
        trialType: ITrialTypeReduxFormState;
        itemComponentType: IItemComponentTypeReduxFormState;
        institution: IInstitutionReduxFormState;
        trialInstitution: ITrialInstitutionReduxFormState;
        trialDoi: ITrialDoiReduxFormState;
        trialWebsite: ITrialWebsiteReduxFormState;
        trialInstitutionItem: ITrialInstitutionItemReduxFormState;
        personnel: IPersonnelReduxFormState;
        personnelRole: IPersonnelRoleReduxFormState;
        searchResult: ISearchResultReduxFormState;
        trialItemImport: ITrialItemImportReduxFormState;
        projectItemImport: IProjectItemImportReduxFormState;
        trialItemUpdate: ITrialItemUpdateReduxFormState;
        openClinicaUserRole: IOpenClinicaUserRoleReduxFormState;
        state: IStateReduxFormState;
        institutionType: IInstitutionTypeReduxFormState;
        siteLocation: ISiteLocationReduxFormState;
        facilityAllianceMember: IFacilityAllianceMemberReduxFormState;
        keyContact: IKeyContactReduxFormState;
        membershipType: IMembershipTypeReduxFormState;
        professionalDiscipline: IProfessionalDisciplineReduxFormState;
        interest: IInterestReduxFormState;
        salutation: ISalutationReduxFormState;
        formTemplate: IFormTemplateReduxFormState;
        trialInstitutionItemCustomView: ITrialInstitutionItemCustomViewReduxFormState;
        role: IRoleReduxFormState;
        department: IDepartmentReduxFormState;
        departmentContact: IDepartmentContactReduxFormState;
        reportingTagCategory: IReportingTagCategoryReduxFormState;
        reportingTag: IReportingTagReduxFormState;
        taskType: ITaskTypeReduxFormState;
        formTemplateFileType: IFormTemplateFileTypeReduxFormState;
        trackerType: ITrackerTypeReduxFormState;
        trackerMilestone: ITrackerMilestoneReduxFormState;
        standardEmailTemplate: IStandardEmailTemplateReduxFormState;
        discussion: IDiscussionReduxFormState;
        projectItem: IProjectItemReduxFormState;
        projectItemTemplate: IProjectItemTemplateReduxFormState;
        projectItemTemplateCategory: IProjectItemTemplateCategoryReduxFormState;
        committee: ICommitteeReduxFormState;
        committeeMeeting: ICommitteeMeetingReduxFormState;
        committeeRegularInvitee: ICommitteeRegularInviteeReduxFormState;
        committeeMeetingAttendance: ICommitteeMeetingAttendanceReduxFormState;
        committeeMember: ICommitteeMemberReduxFormState;
        committeeMemberRole: ICommitteeMemberRoleReduxFormState;
        groupItem: IGroupItemReduxFormState;
        laboratory: ILaboratoryReduxFormState;
        laboratoryDepartment: ILaboratoryDepartmentReduxFormState;
        laboratoryPersonnel: ILaboratoryPersonnelReduxFormState;
        institutionLaboratory: IInstitutionLaboratoryReduxFormState;
        trialInstitutionLaboratory: ITrialInstitutionLaboratoryReduxFormState;
        trialGroupLaboratory: ITrialGroupLaboratoryReduxFormState;
        trialInstitutionLaboratoryPersonnel: ITrialInstitutionLaboratoryPersonnelReduxFormState;
        trialGroupLaboratoryPersonnel: ITrialGroupLaboratoryPersonnelReduxFormState;
        trialItemTemplate: ITrialItemTemplateReduxFormState;
        personnelTraining: IPersonnelTrainingReduxFormState;
        institutionProcedure: IInstitutionProcedureReduxFormState;
        institutionFinance: IInstitutionFinanceReduxFormState;
        institutionContract: IInstitutionContractReduxFormState;
        institutionConfidentialityDeed: IInstitutionConfidentialityDeedReduxFormState;
        laboratoryFinance: ILaboratoryFinanceReduxFormState;
        laboratoryContract: ILaboratoryContractReduxFormState;
        laboratoryConfidentialityDeed: ILaboratoryConfidentialityDeedReduxFormState;
        groupConfidentialityDeed: IGroupConfidentialityDeedReduxFormState;
        trialFinance: ITrialFinanceReduxFormState;
        trialInstitutionFinance: ITrialInstitutionFinanceReduxFormState;
        trialInstitutionContract: ITrialInstitutionContractReduxFormState;
        trialGroupFinance: ITrialGroupFinanceReduxFormState;
        trialGroupContract: ITrialGroupContractReduxFormState;
        trialInstitutionLaboratoryFinance: ITrialInstitutionLaboratoryFinanceReduxFormState;
        trialInstitutionLaboratoryContract: ITrialInstitutionLaboratoryContractReduxFormState;
        trialGroupLaboratoryFinance: ITrialGroupLaboratoryFinanceReduxFormState;
        trialGroupLaboratoryContract: ITrialGroupLaboratoryContractReduxFormState;
        institutionItemTemplateCategory: IInstitutionItemTemplateCategoryReduxFormState;
        institutionItemTemplate: IInstitutionItemTemplateReduxFormState;
        institutionItem: IInstitutionItemReduxFormState;
        trialInstitutionItemTemplateCategory: ITrialInstitutionItemTemplateCategoryReduxFormState;
        trialInstitutionItemTemplate: ITrialInstitutionItemTemplateReduxFormState;
        trialGroupItemTemplateCategory: ITrialGroupItemTemplateCategoryReduxFormState;
        trialGroupItemTemplate: ITrialGroupItemTemplateReduxFormState;
        trialItemCategory: ITrialItemCategoryReduxFormState;
        noteTypeNotes: INoteTypeNotesReduxFormState;
        trialInstitutionItemCategory: ITrialInstitutionItemCategoryReduxFormState;
        trialGroupItemCategory: ITrialGroupItemCategoryReduxFormState;
        institutionItemCategory: IInstitutionItemCategoryReduxFormState;
        groupItemCategory: IGroupItemCategoryReduxFormState;
        monitoring: IMonitoringReduxFormState;
        monitoringIssue: IMonitoringIssueReduxFormState;
        monitor: IMonitorReduxFormState;
        monitoringDocument: IMonitoringDocumentReduxFormState;
        monitoringIssueDocument: IMonitoringIssueDocumentReduxFormState;
        quillDocument: IQuillDocumentReduxFormState;
        quillDocumentImage: IQuillDocumentImageReduxFormState;
    };

}

export interface ITmdState {
    // React states
    routing: RouterState;

    user: IUserState;
    navigation: INavigationDrawerState;
    theme: IThemeState;
    helpDrawer: IHelpDrawerState;

    // Container states
    itemComponentPage: IItemComponentPageState;
    collectionPage: ICollectionPageState;
    trialInstitutionItemsPage: ITrialInstitutionItemsPageState;
    trialsPage: ITrialsPageState;
    trialGroupItemsPage: ITrialGroupItemsPageState;
    togglePage: ITogglePageState;
    pagingPage: IPagingPageState;
    collectionInstanceTrackerPage: ICollectionInstanceTrackerPageState;
    collectionInstanceModalPage: ICollectionInstanceModalPageState;
    groupSettingsPage: IGroupSettingsPageState;
    projectSettingsPage: IProjectSettingsPageState;
    trialSettingsPage: ITrialSettingsPageState;
    groupItemsPage: IGroupItemsPageState;
    trialItemsPage: ITrialItemsPageState;
    institutionItemsPage: IInstitutionItemsPageState;
    trialInstitutionItemCustomViewDataEntryPage: ITrialInstitutionItemCustomViewDataEntryPageState;

    // Model states 
    registrationForms: IRegistrationFormsState;
    reduxForms: IReduxFormsState;
    itemComponent: IItemComponentState;
    categories: ICategoryState;
    countries: ICountryState;
    institutionCodings: IInstitutionCodingState;
    institutionCodes: IInstitutionCodeState;
    noteTypes: INoteTypeState;
    notes: INoteState;
    trialTypes: ITrialTypeState;
    itemComponentTypes: IItemComponentTypeState;
    groups: IGroupState;
    groupItemTemplate: IGroupItemTemplateState;
    groupItemTemplateCategorys: IGroupItemTemplateCategoryState;
    groupContract: IGroupContractState;
    groupFinance: IGroupFinanceState;
    collection: ICollectionState;
    collectionInstance: ICollectionInstanceState;
    trials: ITrialState;
    hrecs: IHrecState;
    healthDistricts: IHealthDistrictState;
    fellowships: IFellowshipState;
    projects: IProjectState;
    projectInvestigator: IProjectInvestigatorState;
    trialItem: ITrialItemState;
    trialGroup: ITrialGroupState;
    trialGroupItem: ITrialGroupItemState;
    trialPersonnel: ITrialPersonnelState;
    institutions: IInstitutionState;
    trialInstitution: ITrialInstitutionState;
    trialDoi: ITrialDoiState;
    trialWebsite: ITrialWebsiteState;
    trialInstitutionItem: ITrialInstitutionItemState;
    personnel: IPersonnelState;
    personnelRole: IPersonnelRoleState;
    search: ISearchState;
    trialItemImport: ITrialItemImportState;
    projectItemImport: IProjectItemImportState;
    trialItemUpdate: ITrialItemUpdateState;
    openClinicaUserRole: IOpenClinicaUserRoleState;
    states: IStateState;
    institutionTypes: IInstitutionTypeState;
    siteLocations: ISiteLocationState;
    facilityAllianceMembers: IFacilityAllianceMemberState;
    keyContacts: IKeyContactState;
    memberStatuses: IMemberStatusState;
    membershipTypes: IMembershipTypeState;
    professionalDisciplines: IProfessionalDisciplineState;
    interests: IInterestState;
    salutations: ISalutationState;
    formTemplates: IFormTemplateState;
    trialInstitutionItemCustomView: ITrialInstitutionItemCustomViewState;
    roles: IRoleState;
    departments: IDepartmentState;
    departmentContacts: IDepartmentContactState;
    reportingTagCategories: IReportingTagCategoryState;
    reportingTags: IReportingTagState;
    taskTypes: ITaskTypeState;
    formTemplateFileTypes: IFormTemplateFileTypeState;
    trackerTypes: ITrackerTypeState;
    trackerMilestones: ITrackerMilestoneState;
    standardEmailTemplates: IStandardEmailTemplateState;
    discussions: IDiscussionState;
    projectItems: IProjectItemState;
    projectItemTemplates: IProjectItemTemplateState;
    projectItemTemplateCategorys: IProjectItemTemplateCategoryState;
    parentTrials: IParentTrialsState;
    committees: ICommitteeState;
    committeeMeeting: ICommitteeMeetingState;
    committeeRegularInvitee: ICommitteeRegularInviteeState;
    committeeMeetingAttendance: ICommitteeMeetingAttendanceState;
    committeeMember: ICommitteeMemberState;
    committeeMemberRole: ICommitteeMemberRoleState;
    groupItems: IGroupItemState;
    projectedAccrual: IProjectedAccrualState;
    accrualANZ: IAccrualANZState;
    laboratories: ILaboratoryState;
    laboratoryDepartments: ILaboratoryDepartmentState;
    laboratoryPersonnels: ILaboratoryPersonnelState;
    institutionLaboratory: IInstitutionLaboratoryState;
    trialInstitutionLaboratory: ITrialInstitutionLaboratoryState;
    trialGroupLaboratory: ITrialGroupLaboratoryState;
    trialInstitutionLaboratoryPersonnel: ITrialInstitutionLaboratoryPersonnelState;
    trialGroupLaboratoryPersonnel: ITrialGroupLaboratoryPersonnelState;
    trialItemTemplate: ITrialItemTemplateState;
    personnelTraining: IPersonnelTrainingState;
    institutionProcedure: IInstitutionProcedureState;
    institutionFinance: IInstitutionFinanceState;
    institutionContract: IInstitutionContractState;
    institutionConfidentialityDeed: IInstitutionConfidentialityDeedState;
    laboratoryFinance: ILaboratoryFinanceState;
    laboratoryContract: ILaboratoryContractState;
    laboratoryConfidentialityDeed: ILaboratoryConfidentialityDeedState;
    groupConfidentialityDeed: IGroupConfidentialityDeedState;
    trialFinance: ITrialFinanceState;
    trialInstitutionFinance: ITrialInstitutionFinanceState;
    trialInstitutionContract: ITrialInstitutionContractState;
    trialGroupFinance: ITrialGroupFinanceState;
    trialGroupContract: ITrialGroupContractState;
    trialInstitutionLaboratoryFinance: ITrialInstitutionLaboratoryFinanceState;
    trialInstitutionLaboratoryContract: ITrialInstitutionLaboratoryContractState;
    trialGroupLaboratoryFinance: ITrialGroupLaboratoryFinanceState;
    trialGroupLaboratoryContract: ITrialGroupLaboratoryContractState;
    institutionItemTemplateCategories: IInstitutionItemTemplateCategoryState;
    institutionItemTemplate: IInstitutionItemTemplateState;
    institutionItem: IInstitutionItemState;
    trialInstitutionItemTemplateCategory: ITrialInstitutionItemTemplateCategoryState;
    trialInstitutionItemTemplate: ITrialInstitutionItemTemplateState;
    trialGroupItemTemplateCategory: ITrialGroupItemTemplateCategoryState;
    trialGroupItemTemplate: ITrialGroupItemTemplateState;
    trialItemCategory: ITrialItemCategoryState;
    noteTypeNotes: INoteTypeNotesState;
    trialInstitutionItemCategory: ITrialInstitutionItemCategoryState;
    trialGroupItemCategory: ITrialGroupItemCategoryState;
    institutionItemCategory: IInstitutionItemCategoryState;
    groupItemCategory: IGroupItemCategoryState;
    monitoring: IMonitoringState;
    monitoringIssue: IMonitoringIssueState;
    monitor: IMonitorState;
    monitoringDocument: IMonitoringDocumentState;
    monitoringIssueDocument: IMonitoringIssueDocumentState;
    patients: IPatientState;
    quillDocument: IQuillDocumentState;
    quillDocumentImage: IQuillDocumentImageState;
}

/** Combines all reducers into one for the overall state.
*   We can place all related reducers together (in order) so that they are
*   easier to find whilst debugging (redux dev tools)
*/
export default combineReducers({
    // React reducers
    routing: RoutingReducer,

    user: user,
    navigation: navigation,
    theme: theme,
    helpDrawer: helpDrawer,

    // Container reducers
    itemComponentPage: itemComponentPage,
    collectionPage: collectionPage,
    trialInstitutionItemsPage: trialInstitutionItemsPage,
    trialsPage: trialsPage,
    trialGroupItemsPage: trialGroupItemsPage,
    togglePage: togglePage,
    pagingPage: pagingPage,
    collectionInstanceTrackerPage: collectionInstanceTrackerPage,
    collectionInstanceModalPage: collectionInstanceModalPage,
    groupSettingsPage: groupSettingsPage,
    projectSettingsPage: projectSettingsPage,
    trialSettingsPage: trialSettingsPage,
    groupItemsPage: groupItemsPage,
    trialItemsPage: trialItemsPage,
    institutionItemsPage: institutionItemsPage,
    trialInstitutionItemCustomViewDataEntryPage: trialInstitutionItemCustomViewDataEntryPage,

    // API Model reducers
    trials: trials,
    hrecs: hrecs,
    healthDistricts: healthDistricts,
    fellowships: fellowships,
    parentTrials: parentTrials,
    projects: projects,
    projectInvestigator: projectInvestigator,
    itemComponent: itemComponents,
    categories: categories,
    countries: countries,
    institutionCodings: institutionCodings,
    institutionCodes: institutionCodes,
    noteTypes: noteTypes,
    notes: notes,
    trialTypes: trialTypes,
    itemComponentTypes: itemComponentTypes,
    groups: groups,
    groupItemTemplate: groupItemTemplate,
    groupItemTemplateCategorys: groupItemTemplateCategorys,
    groupContract: groupContracts,
    groupFinance: groupFinances,
    collection: collection,
    collectionInstance: collectionInstance,
    trialItem: trialItem,
    trialGroup: trialGroup,
    trialGroupItem: trialGroupItem,
    trialPersonnel: trialPersonnel,
    institutions: institutions,
    trialInstitution: trialInstitution,
    trialDoi: trialDoi,
    trialWebsite: trialWebsite,
    trialInstitutionItem: trialInstitutionItem,
    personnel: personnel,
    personnelRole: personnelRole,
    search: search,
    trialItemImport: trialItemImport,
    projectItemImport: projectItemImport,
    trialItemUpdate: trialItemUpdate,
    openClinicaUserRole: openClinicaUserRole,
    states: states,
    institutionTypes: institutionTypes,
    siteLocations: siteLocations,
    facilityAllianceMembers: facilityAllianceMembers,
    keyContacts: keyContacts,
    membershipTypes: membershipTypes,
    memberStatuses: memberStatuses,
    professionalDisciplines: professionalDisciplines,
    interests: interests,
    salutations: salutations,
    formTemplates: formTemplates,
    trialInstitutionItemCustomView: trialInstitutionItemCustomView,
    roles: roles,
    departments: departments,
    departmentContacts: departmentContacts,
    reportingTagCategories: reportingTagCategories,
    reportingTags: reportingTags,
    taskTypes: taskTypes,
    formTemplateFileTypes: formTemplateFileTypes,
    trackerTypes: trackerTypes,
    trackerMilestones: trackerMilestones,
    standardEmailTemplates: standardEmailTemplates,
    discussions: discussions,
    projectItems: projectItems,
    projectItemTemplates: projectItemTemplates,
    projectItemTemplateCategorys: projectItemTemplateCategorys,
    committees: committees,
    committeeMeeting: committeeMeetings,
    committeeRegularInvitee: committeeRegularInvitees,
    committeeMeetingAttendance: committeeMeetingAttendances,
    committeeMember: committeeMembers,
    committeeMemberRole: committeeMemberRoles,
    groupItems: groupItems,
    projectedAccrual: projectedAccrual,
    accrualANZ: accrualANZ,
    laboratories: laboratories,
    laboratoryDepartments: laboratoryDepartments,
    laboratoryPersonnels: laboratoryPersonnels,
    institutionLaboratory: institutionLaboratory,
    trialInstitutionLaboratory: trialInstitutionLaboratory,
    trialGroupLaboratory: trialGroupLaboratory,
    trialInstitutionLaboratoryPersonnel: trialInstitutionLaboratoryPersonnel,
    trialGroupLaboratoryPersonnel: trialGroupLaboratoryPersonnels,
    trialItemTemplate: trialItemTemplate,
    personnelTraining: personnelTraining,
    institutionProcedure: institutionProcedure,
    institutionFinance: institutionFinance,
    institutionContract: institutionContract,
    institutionConfidentialityDeed: institutionConfidentialityDeed,
    laboratoryFinance: laboratoryFinance,
    laboratoryContract: laboratoryContract,
    laboratoryConfidentialityDeed: laboratoryConfidentialityDeed,
    groupConfidentialityDeed: groupConfidentialityDeed,
    trialFinance: trialFinance,
    trialInstitutionFinance: trialInstitutionFinance,
    trialInstitutionContract: trialInstitutionContract,
    trialGroupFinance: trialGroupFinance,
    trialGroupContract: trialGroupContract,
    trialInstitutionLaboratoryFinance: trialInstitutionLaboratoryFinance,
    trialInstitutionLaboratoryContract: trialInstitutionLaboratoryContract,
    trialGroupLaboratoryFinance: trialGroupLaboratoryFinance,
    trialGroupLaboratoryContract: trialGroupLaboratoryContract,
    institutionItemTemplateCategories: institutionItemTemplateCategories,
    institutionItemTemplate: institutionItemTemplate,
    institutionItem: institutionItem,
    trialInstitutionItemTemplateCategory: trialInstitutionItemTemplateCategory,
    trialInstitutionItemTemplate: trialInstitutionItemTemplate,
    trialGroupItemTemplateCategory: trialGroupItemTemplateCategory,
    trialGroupItemTemplate: trialGroupItemTemplate,
    trialItemCategory: trialItemCategory,
    noteTypeNotes: noteTypeNotes,
    trialInstitutionItemCategory: trialInstitutionItemCategory,
    trialGroupItemCategory: trialGroupItemCategory,
    institutionItemCategory: institutionItemCategory,
    groupItemCategory: groupItemCategory,
    monitoring: monitoring,
    monitoringIssue: monitoringIssue,
    monitor: monitor,
    monitoringDocument: monitoringDocument,
    monitoringIssueDocument: monitoringIssueDocument,
    patients: patients,
    quillDocument: quillDocument,
    quillDocumentImage: quillDocumentImage,

    // registrationForms: combineReducers({
    //     preRegistrationForms
    // }),
    // Redux Forms. Avoid clutter in dev tools by placing down the bottom.
    reduxForms: combineForms({
        itemComponent: initialItemComponentState,
        collection: initialCollectionState,
        collectionInstance: initialCollectionInstanceState,
        trial: initialTrialState,
        hrec: initialHrecState,
        healthDistrict: initialHealthDistrictState,
        fellowship: initialFellowshipState,
        project: initialProjectState,
        projectInvestigator: initialProjectInvestigatorState,
        trialItem: initialTrialItemState,
        trialGroup: initialTrialGroupState,
        trialGroupItem: initialTrialGroupItemState,
        trialPersonnel: initialTrialPersonnelState,
        group: initialGroupState,
        groupItemTemplate: initialGroupItemTemplateState,
        groupItemTemplateCategory: initialGroupItemTemplateCategoryState,
        groupContract: initialGroupContractState,
        groupFinance: initialGroupFinanceState,
        category: initialCategoryState,
        country: initialCountryState,
        institutionCoding: initialInstitutionCodingState,
        institutionCode: initialInstitutionCodeState,
        noteType: initialNoteTypeState,
        note: initialNoteState,
        trialType: initialTrialTypeState,
        itemComponentType: initialItemComponentTypeState,
        institution: initialInstitutionState,
        trialInstitution: initialTrialInstitutionState,
        trialDoi: initialTrialDoiState,
        trialWebsite: initialTrialWebsiteState,
        trialInstitutionItem: initialTrialInstitutionItemState,
        personnel: initialPersonnelState,
        personnelRole: initialPersonnelRoleState,
        searchResult: initialSearchResultState,
        trialItemImport: initialTrialItemImportState,
        projectItemImport: initialProjectItemImportState,
        trialItemUpdate: initialTrialItemUpdateState,
        openClinicaUserRole: initialOpenClinicaUserRoleState,
        state: initialStateState,
        institutionType: initialInstitutionTypeState,
        siteLocation: initialSiteLocationState,
        facilityAllianceMember: initialFacilityAllianceMemberState,
        keyContact: initialKeyContactState,
        membershipType: initialMembershipTypeState,
        professionalDiscipline: initialProfessionalDisciplineState,
        interest: initialInterestState,
        salutation: initialSalutationState,
        formTemplate: initialFormTemplateState,
        trialInstitutionItemCustomView: initialTrialInstitutionItemCustomViewState,
        role: initialRoleState,
        department: initialDepartmentState,
        departmentContact: initialDepartmentContactState,
        reportingTagCategory: initialReportingTagCategoryState,
        reportingTag: initialReportingTagState,
        taskType: initialTaskTypeState,
        formTemplateFileType: initialFormTemplateFileTypeState,
        trackerType: initialTrackerTypeState,
        trackerMilestone: initialTrackerMilestoneState,
        standardEmailTemplate: initialStandardEmailTemplateState,
        discussion: initialDiscussionState,
        projectItem: initialProjectItemState,
        projectItemTemplate: initialProjectItemTemplateState,
        projectItemTemplateCategory: initialProjectItemTemplateCategoryState,
        committee: initialCommitteeState,
        committeeMeeting: initialCommitteeMeetingState,
        committeeRegularInvitee: initialCommitteeRegularInviteeState,
        committeeMeetingAttendance: initialCommitteeMeetingAttendanceState,
        committeeMember: initialCommitteeMemberState,
        committeeMemberRole: initialCommitteeMemberRoleState,
        groupItem: initialGroupItemState,
        laboratory: initialLaboratoryState,
        laboratoryDepartment: initialLaboratoryDepartmentState,
        laboratoryPersonnel: initialLaboratoryPersonnelState,
        institutionLaboratory: initialInstitutionLaboratoryState,
        trialInstitutionLaboratory: initialTrialInstitutionLaboratoryState,
        trialGroupLaboratory: initialTrialGroupLaboratoryState,
        trialInstitutionLaboratoryPersonnel: initialTrialInstitutionLaboratoryPersonnelState,
        trialGroupLaboratoryPersonnel: initialTrialGroupLaboratoryPersonnelState,
        trialItemTemplate: initialTrialItemTemplateState,
        personnelTraining: initialPersonnelTrainingState,
        institutionProcedure: initialInstitutionProcedureState,
        institutionFinance: initialInstitutionFinanceState,
        institutionContract: initialInstitutionContractState,
        institutionConfidentialityDeed: initialInstitutionConfidentialityDeedState,
        laboratoryFinance: initialLaboratoryFinanceState,
        laboratoryContract: initialLaboratoryContractState,
        laboratoryConfidentialityDeed: initialLaboratoryConfidentialityDeedState,
        groupConfidentialityDeed: initialGroupConfidentialityDeedState,
        trialFinance: initialTrialFinanceState,
        trialInstitutionFinance: initialTrialInstitutionFinanceState,
        trialInstitutionContract: initialTrialInstitutionContractState,
        trialGroupFinance: initialTrialGroupFinanceState,
        trialGroupContract: initialTrialGroupContractState,
        trialInstitutionLaboratoryFinance: initialTrialInstitutionLaboratoryFinanceState,
        trialInstitutionLaboratoryContract: initialTrialInstitutionLaboratoryContractState,
        trialGroupLaboratoryFinance: initialTrialGroupLaboratoryFinanceState,
        trialGroupLaboratoryContract: initialTrialGroupLaboratoryContractState,
        institutionItemTemplateCategory: initialInstitutionItemTemplateCategoryState,
        institutionItemTemplate: initialInstitutionItemTemplateState,
        institutionItem: initialInstitutionItemState,
        trialInstitutionItemTemplateCategory: initialTrialInstitutionItemTemplateCategoryState,
        trialInstitutionItemTemplate: initialTrialInstitutionItemTemplateState,
        trialGroupItemTemplateCategory: initialTrialGroupItemTemplateCategoryState,
        trialGroupItemTemplate: initialTrialGroupItemTemplateState,
        trialItemCategory: initialTrialItemCategoryState,
        noteTypeNotes: initialNoteTypeNotesState,
        trialInstitutionItemCategory: initialTrialInstitutionItemCategoryState,
        trialGroupItemCategory: initialTrialGroupItemCategoryState,
        institutionItemCategory: initialInstitutionItemCategoryState,
        groupItemCategory: initialGroupItemCategoryState,
        monitoring: initialMonitoringState,
        monitoringIssue: initialMonitoringIssueState,
        monitor: initialMonitorState,
        monitoringDocument: initialMonitoringDocumentState,
        monitoringIssueDocument: initialMonitoringIssueDocumentState,
        quillDocument: initialQuillDocumentState,
        quillDocumentImage: initialQuillDocumentImageState,
    }, 'reduxForms', { key: 'formState' })
});